import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core'
import { firstValueFrom, forkJoin } from 'rxjs'

import { appConstants } from '../../../app.constants'
import { IncentiveMode } from '../../../shared/enums/incentive-mode.enum'
import { Incentive } from '../../../shared/interfaces/resources/incentive.interface'
import { Theme } from '../../../shared/interfaces/resources/theme.interface'
import { FavoriteService } from '../../../shared/services/favorite.service'
import { IncentiveService } from '../../../shared/services/incentive.service'
import { ThemeService } from '../../../shared/services/theme.service'

@Component({
  selector: 'app-incentive-comparator',
  templateUrl: './incentive-comparator.component.html',
  styleUrls: ['./incentive-comparator.component.scss']
})
export class IncentiveComparatorComponent implements OnChanges {
  @Input() incentiveIds: string[]
  @Input() mode: IncentiveMode = IncentiveMode.Comparison
  @Input() isOrderable: boolean = false
  @Input() isFavoriteButtonDisplayed: boolean = false
  @Input() isShowSimilarDisplayed: boolean = false
  @Input() allowDeleteIncentive: boolean = false
  @Input() showAdviceIfEmpty: boolean = false
  @Input() sortBy: string

  @Output() orderChanged: EventEmitter<Incentive[]> = new EventEmitter()

  @ViewChildren('valueField') valueFields: QueryList<any>

  incentives: Incentive[]
  themes: Theme[]

  OECDmedianId: string = appConstants.incentives.OECDmedianId
  EUmedianId: string = appConstants.incentives.EUmedianId

  showOECDMedian: boolean = false
  showEUMedian: boolean = false
  incentiveOECDMedian: Incentive
  incentiveEUMedian: Incentive

  themeVisibilityDialogOpen: string
  loading: boolean = false

  isOrderIncentivesModalOpen: boolean = false

  isCarouselActive: boolean = false
  carouselIndex = 0
  visibleIncentiveCount: number = 0
  maxVisibleIncentives: number

  Mode = IncentiveMode

  constructor(
    private incentiveService: IncentiveService,
    private favoriteService: FavoriteService,
    private themeService: ThemeService
  ) {}

  ngOnChanges(): void {
    if (!this.incentiveIds || this.incentiveIds.length === 0) {
      this.incentives = []
      return
    }

    this.loading = true

    this.showOECDMedian = false
    this.showEUMedian = false

    this.incentiveService
      .listWithoutPagination({
        mode: this.mode,
        sortBy: this.sortBy,
        ids: this.incentiveIds
      })
      .then(
        (incentiveRes: Incentive[]) => {
          if (this.incentiveIds && !this.sortBy) {
            this.incentives = this.orderIncentivesByIds(
              this.incentiveIds,
              incentiveRes
            )
          } else {
            this.incentives = incentiveRes
          }

          firstValueFrom(
            forkJoin([
              this.incentiveService.show(this.EUmedianId),
              this.incentiveService.show(this.OECDmedianId)
            ])
          ).then(([EUMedianIncentive, OECDMedianRes]) => {
            this.incentiveEUMedian = EUMedianIncentive
            this.incentiveOECDMedian = OECDMedianRes

            this.incentives.unshift(EUMedianIncentive)
            this.incentives.unshift(OECDMedianRes)

            this.initCarousel()
            this.initTruncateMultiLineValues()

            this.loading = false
          })
        },
        (err) => {
          this.loading = false
        }
      )

    this.themeService.list({ mode: this.mode }).then((themeRes: Theme[]) => {
      this.themes = themeRes
    })
  }

  removeIncentiveFromComparator(incentiveIdToDelete: string) {
    this.favoriteService.remove(incentiveIdToDelete)

    this.incentives = this.incentives.filter(
      (incentive: Incentive) => incentive.id !== incentiveIdToDelete
    )
    this.visibleIncentiveCount = this.getVisibleIncentiveCount()
  }

  hasVisibleSubFields(theme: Theme): boolean {
    return theme.fields.some((f) => !f.isHidden)
  }

  toggleThemeVisibility(themeId: string) {
    if (this.themeVisibilityDialogOpen === themeId) {
      this.themeVisibilityDialogOpen = ''
    } else {
      this.themeVisibilityDialogOpen = themeId
    }
  }

  // Setup carousel based on window width and number of visible incentives.
  initCarousel() {
    this.visibleIncentiveCount = this.getVisibleIncentiveCount()

    const incentiveWidth = 280
    const sidebarWidth = 254

    switch (true) {
      // Mobile.
      case window.innerWidth < appConstants.windowBreakpoints.mobile &&
        this.visibleIncentiveCount > 1:
        this.isCarouselActive = true
        this.maxVisibleIncentives = 1
        break
      // Tablet and desktop.
      case window.innerWidth < appConstants.windowBreakpoints.fullHD &&
        this.visibleIncentiveCount > 3:
        this.isCarouselActive = true
        this.maxVisibleIncentives = 3
        break
      // Full HD.
      case window.innerWidth >= appConstants.windowBreakpoints.fullHD &&
        this.visibleIncentiveCount * incentiveWidth >
          window.innerWidth - sidebarWidth:
        this.isCarouselActive = true
        this.maxVisibleIncentives = Math.floor(
          (window.innerWidth - sidebarWidth) / incentiveWidth
        )
        break
      default:
        this.isCarouselActive = false
    }
  }

  // Truncate multi-line values based on field size and content.
  initTruncateMultiLineValues() {
    setTimeout(() => {
      this.valueFields.forEach((vF) => {
        // Get line height of the field. We need to add 1px to the value to avoid miscalculations on mobile.
        const lineHeight =
          Math.floor(
            parseFloat(
              window
                .getComputedStyle(vF.nativeElement)
                .lineHeight.substring(0, 2)
            )
          ) + 1

        if (vF.nativeElement.offsetHeight > lineHeight) {
          vF.nativeElement.classList.add(
            'has-multiline-content',
            'is-truncated'
          )
        }
      })
    }, 100)
  }

  getVisibleIncentiveCount() {
    return (
      this.incentives.length -
      (this.showEUMedian ? 0 : 1) -
      (this.showOECDMedian ? 0 : 1)
    )
  }

  // Extend or truncate multi-line values. The whole line is concerned (includes values of the same field in other incentives).
  toggleExtendValue(elemClassName: string) {
    const valueFields: HTMLCollectionOf<Element> =
      document.getElementsByClassName(elemClassName)
    Array.from(valueFields).forEach((elem: Element) => {
      elem.classList.toggle('is-truncated')
    })
  }

  onOrderChanged(incentiveIdsInOrder: string[]) {
    this.isOrderIncentivesModalOpen = false
    if (incentiveIdsInOrder.length) {
      this.incentives = this.orderIncentivesByIds(
        incentiveIdsInOrder,
        this.incentives
      )

      this.incentives.unshift(this.incentiveEUMedian)
      this.incentives.unshift(this.incentiveOECDMedian)

      this.orderChanged.emit(this.incentives)
    }
  }

  private orderIncentivesByIds(
    incentiveIdsInOrder: string[],
    incentives: Incentive[]
  ): Incentive[] {
    return incentiveIdsInOrder.map((id: string) =>
      incentives.find((i: Incentive) => i.id === id)
    )
  }

  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget: any) {
    if (
      eventTarget.className.includes('dropdown-item') ||
      eventTarget.className.includes('fa-solid fa-sliders')
    ) {
    } else {
      this.themeVisibilityDialogOpen = ''
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.initCarousel()
    this.initTruncateMultiLineValues()
  }
}
